import type { Condition, ComparisonOperator } from "@/program/Condition";
import {
  OrbColor,
  ORB_COLORS,
  ORB_COLOR_LABELS,
} from "@/program/VariableState";

/**
 * オーブ比較で選択できる値の選択肢
 *
 * 0-3: 基礎ステージで使用する小さな値
 * 5, 10, 20: 上級ステージで使用する大きな値
 * ステージ設計に合わせて調整する
 */
const ORB_VALUE_OPTIONS = [0, 1, 2, 3, 5, 10, 20] as const;

/**
 * 比較演算子の選択肢
 */
const OPERATORS: { value: ComparisonOperator; label: string }[] = [
  { value: "==", label: "＝" },
  { value: "!=", label: "≠" },
  { value: ">", label: "＞" },
  { value: "<", label: "＜" },
];

/**
 * 条件選択モーダル（if カード用）
 *
 * 壁判定とオーブ比較をピル型タブで切り替える。
 * タブ切り替え時は他方のタブの選択をリセットする。
 * OrbSelectorModal と同じ overlay + dialog パターン。
 */
export class ConditionSelectorModal {
  private overlay: HTMLElement;
  private modal: HTMLElement;
  private onSelectCallback?: (condition: Condition) => void;
  private onCloseCallback?: () => void;
  private isOpen: boolean = false;
  private activeTab: "wall" | "orb" = "wall";
  private selectedColor: OrbColor | null = null;
  private selectedOperator: ComparisonOperator | null = null;
  private selectedValue: number | null = null;
  private selectedWall: "wallAhead" | "noWallAhead" | null = null;
  private previousFocus: HTMLElement | null = null;

  constructor() {
    this.overlay = document.createElement("div");
    this.overlay.className = "condition-modal-overlay";
    this.overlay.addEventListener("click", () => this.close());

    this.modal = document.createElement("div");
    this.modal.className = "condition-modal";
    this.modal.setAttribute("role", "dialog");
    this.modal.setAttribute("aria-modal", "true");
    this.modal.setAttribute("aria-label", "条件を選択");
    this.modal.addEventListener("click", (e) => e.stopPropagation());

    this.overlay.appendChild(this.modal);
  }

  /**
   * モーダルを開く
   */
  open(currentCondition?: Condition): void {
    if (this.isOpen) return;
    this.isOpen = true;

    // 既存の条件があればそれを初期値にする
    if (currentCondition?.type === "orbComparison") {
      this.activeTab = "orb";
      this.selectedColor = currentCondition.orbColor;
      this.selectedOperator = currentCondition.operator;
      this.selectedValue = currentCondition.value;
      this.selectedWall = null;
    } else if (
      currentCondition?.type === "wallAhead" ||
      currentCondition?.type === "noWallAhead"
    ) {
      this.activeTab = "wall";
      this.selectedWall = currentCondition.type;
      this.selectedColor = null;
      this.selectedOperator = null;
      this.selectedValue = null;
    } else {
      this.activeTab = "wall";
      this.selectedColor = "green";
      this.selectedOperator = "==";
      this.selectedValue = 0;
      this.selectedWall = "wallAhead";
    }

    this.previousFocus = document.activeElement as HTMLElement | null;
    this.render();
    document.body.appendChild(this.overlay);

    document.addEventListener("keydown", this.handleKeyDown);

    requestAnimationFrame(() => {
      const first = this.modal.querySelector("button:not([disabled])");
      if (first instanceof HTMLElement) first.focus();
    });
  }

  close(): void {
    if (!this.isOpen) return;
    this.isOpen = false;

    this.overlay.remove();
    document.removeEventListener("keydown", this.handleKeyDown);
    this.previousFocus?.focus();
    this.onCloseCallback?.();
  }

  getIsOpen(): boolean {
    return this.isOpen;
  }

  onSelect(callback: (condition: Condition) => void): void {
    this.onSelectCallback = callback;
  }

  onClose(callback: () => void): void {
    this.onCloseCallback = callback;
  }

  private handleKeyDown = (e: KeyboardEvent): void => {
    if (e.key === "Escape") {
      this.close();
    } else if (e.key === "Tab") {
      this.trapFocus(e);
    }
  };

  private trapFocus(e: KeyboardEvent): void {
    const focusable = this.modal.querySelectorAll("button:not([disabled])");
    if (focusable.length === 0) return;

    const first = focusable[0] as HTMLElement;
    const last = focusable[focusable.length - 1] as HTMLElement;

    if (e.shiftKey && document.activeElement === first) {
      e.preventDefault();
      last.focus();
    } else if (!e.shiftKey && document.activeElement === last) {
      e.preventDefault();
      first.focus();
    }
  }

  private selectCondition(condition: Condition): void {
    this.onSelectCallback?.(condition);
    this.close();
  }

  private render(): void {
    const children: HTMLElement[] = [];

    // タイトル
    const title = document.createElement("div");
    title.className = "condition-modal__title";
    title.textContent = "条件を選択";
    children.push(title);

    // === タブバー（ピル型） ===
    const tabs = document.createElement("div");
    tabs.className = "condition-modal__tabs";
    tabs.setAttribute("role", "tablist");

    const tabDefs: { id: "wall" | "orb"; label: string }[] = [
      { id: "wall", label: "壁判定" },
      { id: "orb", label: "オーブ比較" },
    ];

    for (const tab of tabDefs) {
      const tabBtn = document.createElement("button");
      tabBtn.className = "condition-modal__tab";
      if (tab.id === this.activeTab) {
        tabBtn.classList.add("condition-modal__tab--active");
      }
      tabBtn.setAttribute("role", "tab");
      tabBtn.setAttribute("aria-selected", String(tab.id === this.activeTab));
      tabBtn.setAttribute("data-tab", tab.id);
      tabBtn.textContent = tab.label;
      tabBtn.addEventListener("click", () => {
        if (this.activeTab === tab.id) return;
        this.activeTab = tab.id;
        // タブ切り替え時は他方の選択をデフォルト値にリセット
        if (tab.id === "wall") {
          this.selectedColor = null;
          this.selectedOperator = null;
          this.selectedValue = null;
          this.selectedWall = "wallAhead";
        } else {
          this.selectedWall = null;
          this.selectedColor = "green";
          this.selectedOperator = "==";
          this.selectedValue = 0;
        }
        this.render();
      });
      tabs.appendChild(tabBtn);
    }

    children.push(tabs);

    // === 壁判定タブコンテンツ ===
    if (this.activeTab === "wall") {
      const wallPanel = document.createElement("div");
      wallPanel.setAttribute("role", "tabpanel");

      const wallGrid = document.createElement("div");
      wallGrid.className = "condition-modal__wall-grid";

      const wallOptions: {
        type: "wallAhead" | "noWallAhead";
        label: string;
      }[] = [
        { type: "wallAhead", label: "前に壁がある" },
        { type: "noWallAhead", label: "前に壁がない" },
      ];

      for (const option of wallOptions) {
        const button = document.createElement("button");
        button.className = "condition-modal__wall-option";
        if (option.type === this.selectedWall) {
          button.classList.add("condition-modal__wall-option--selected");
        }
        button.setAttribute("data-condition", option.type);
        button.setAttribute("aria-label", option.label);
        button.setAttribute(
          "aria-pressed",
          String(option.type === this.selectedWall)
        );
        button.textContent = option.label;
        button.addEventListener("click", () => {
          this.selectedWall = option.type;
          this.render();
        });
        wallGrid.appendChild(button);
      }

      wallPanel.appendChild(wallGrid);
      children.push(wallPanel);
    }

    // === オーブ比較タブコンテンツ ===
    if (this.activeTab === "orb") {
      const orbPanel = document.createElement("div");
      orbPanel.setAttribute("role", "tabpanel");

      // 色選択
      const colorGrid = document.createElement("div");
      colorGrid.className = "condition-modal__color-grid";

      for (const color of ORB_COLORS) {
        const button = document.createElement("button");
        button.className = "condition-modal__color-option";
        if (color === this.selectedColor) {
          button.classList.add("condition-modal__color-option--selected");
        }
        button.setAttribute("data-color", color);
        button.setAttribute("aria-label", `${ORB_COLOR_LABELS[color]}オーブ`);
        button.setAttribute(
          "aria-pressed",
          String(color === this.selectedColor)
        );

        const indicator = document.createElement("span");
        indicator.className = `condition-modal__color-indicator orb-color--${color} orb-glow`;
        button.appendChild(indicator);

        const label = document.createElement("span");
        label.textContent = ORB_COLOR_LABELS[color];
        button.appendChild(label);

        button.addEventListener("click", () => {
          this.selectedColor = color;
          this.render();
        });

        colorGrid.appendChild(button);
      }

      orbPanel.appendChild(colorGrid);

      // 演算子選択
      const operatorGrid = document.createElement("div");
      operatorGrid.className = "condition-modal__operator-grid";

      for (const op of OPERATORS) {
        const button = document.createElement("button");
        button.className = "condition-modal__operator-option";
        if (op.value === this.selectedOperator) {
          button.classList.add("condition-modal__operator-option--selected");
        }
        button.setAttribute("data-operator", op.value);
        button.setAttribute("aria-label", `演算子 ${op.label}`);
        button.setAttribute(
          "aria-pressed",
          String(op.value === this.selectedOperator)
        );
        button.textContent = op.label;

        button.addEventListener("click", () => {
          this.selectedOperator = op.value;
          this.render();
        });

        operatorGrid.appendChild(button);
      }

      orbPanel.appendChild(operatorGrid);

      // 値選択
      const valueGrid = document.createElement("div");
      valueGrid.className = "condition-modal__value-grid";

      for (const value of ORB_VALUE_OPTIONS) {
        const button = document.createElement("button");
        button.className = "condition-modal__value-option";
        if (value === this.selectedValue) {
          button.classList.add("condition-modal__value-option--selected");
        }
        button.setAttribute("data-value", String(value));
        button.setAttribute("aria-label", `値 ${value}`);
        button.setAttribute(
          "aria-pressed",
          String(value === this.selectedValue)
        );
        button.textContent = String(value);

        button.addEventListener("click", () => {
          this.selectedValue = value;
          this.render();
        });

        valueGrid.appendChild(button);
      }

      orbPanel.appendChild(valueGrid);
      children.push(orbPanel);
    }

    // 確定ボタン（壁判定またはオーブ比較が選択済みのとき有効）
    const confirmBtn = document.createElement("button");
    confirmBtn.className = "condition-modal__confirm";
    confirmBtn.textContent = "決定";
    const orbReady =
      this.selectedColor !== null &&
      this.selectedOperator !== null &&
      this.selectedValue !== null;
    const canConfirm = this.selectedWall !== null || orbReady;
    confirmBtn.disabled = !canConfirm;
    confirmBtn.addEventListener("click", () => {
      if (this.selectedWall !== null) {
        this.selectCondition({ type: this.selectedWall });
      } else if (
        this.selectedColor !== null &&
        this.selectedOperator !== null &&
        this.selectedValue !== null
      ) {
        this.selectCondition({
          type: "orbComparison",
          orbColor: this.selectedColor,
          operator: this.selectedOperator,
          value: this.selectedValue,
        });
      }
    });
    children.push(confirmBtn);

    // キャンセル
    const closeBtn = document.createElement("button");
    closeBtn.className = "condition-modal__close";
    closeBtn.textContent = "キャンセル";
    closeBtn.addEventListener("click", () => this.close());
    children.push(closeBtn);

    this.modal.replaceChildren(...children);
  }
}
